import React, { useCallback, useEffect, useState, useContext } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Button,
  IconButton,
  Box,
  Stack,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import { DeleteOutline, Add } from '@mui/icons-material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import TitleIcon from '@mui/icons-material/Title'
import { v4 as uuidv4 } from 'uuid'
import { OfrecerPremiumContext } from '../../../context/OfrecerPremium/ofrecerPremium.context'

// Cropper
import ReactCrop, { Crop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import './editor.css'

// d&d
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import ExpandImageDialog from './ExpandImage'
import AddTextDialog from './AddTextDialog'
import { useAnalytics } from '../../../hooks/useAnalytics'

const DROPPER_ERROR_LABELS = {
  maxPhotos: 'too-many-files',
  invalidFile: 'file-invalid-type'
}

export const MAX_IMAGE_SIZE_MB = process.env.REACT_APP_MAIN_URL.includes('qa')
  ? Number(process.env.REACT_APP_MAX_IMAGE_SIZE_DEV)
  : Number(process.env.REACT_APP_MAX_IMAGE_SIZE_PROD)

export const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024

const Dropzone = ({ images, setImages, maxPhotos, ofrecerPremium, cta }: any) => {
  const { onOpen } = useContext(OfrecerPremiumContext)
  const [expandImage, setExpandImage] = useState<boolean>(false)
  const [addText, setAddText] = useState<boolean>(false)
  const [desc, setDesc] = useState<string>('')
  const [showIcons, setShowIcons] = useState<boolean>(false)
  const [imgSelected, setImgSelected] = useState<string | null>(null)
  const [indexSelected, setIndexSelected] = useState<number>(null)

  const [toEdit, setToEdit] = useState<any>()
  const [crop, setCrop] = useState<Crop>()
  const [output, setOutput] = useState<any>(null)
  const [dropError, setDropError] = useState<string>()
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { track } = useAnalytics()

  const onDrop = useCallback(
    async (acceptedFiles: any) => {
      setIsDragging(false)
      if (!acceptedFiles) return

      for (const file of acceptedFiles) {
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          return setDropError(`El tamaño máximo de la imagen es de ${MAX_IMAGE_SIZE_MB}MB`)
        }

        // Validar cantidad máxima de fotos
        if (maxPhotos && images?.length >= maxPhotos) {
          return setDropError(`La cantidad máxima de fotos es: ${maxPhotos}`)
        }

        // Leer como ArrayBuffer
        const readerArrayBuffer = new FileReader()
        readerArrayBuffer.onabort = () => console.log('file reading was aborted')
        readerArrayBuffer.onerror = () => console.log('file reading has failed')
        readerArrayBuffer.onload = async () => {
          const binaryStr: any = readerArrayBuffer.result
          const blob: any = new Blob([binaryStr])
          const url = URL.createObjectURL(blob)

          // Leer como base64
          const readerBase64 = new FileReader()
          readerBase64.onload = () => {
            const base64String = readerBase64.result as string

            setImages((images: any) => [
              ...images,
              {
                id: uuidv4(),
                desc: file.name,
                cta: desc && desc !== '' ? desc : null,
                url,
                blob,
                bin: binaryStr,
                raw: file,
                base64: base64String
              }
            ])
          }
          readerBase64.readAsDataURL(file)
        }
        readerArrayBuffer.readAsArrayBuffer(file)
      }
    },
    [images, desc, maxPhotos]
  )

  const handleToEdit = (id: string) => {
    const img = images.find((a: any) => a.id === id)
    setToEdit(img)
  }

  const handleSave = (id: string) => {
    const update = images.find((a: any) => a.id === id)

    setToEdit(undefined)
    console.log('ENCONTRADO PARA GUARDAR', update)
    console.log('CORTE GUARDADO', crop)
  }

  const handleDelete = (id?: string | number, index?: number) => {
    if (typeof id === 'number') {
      // eliminar por id numérico
      setImages(images.filter((img: any) => img.idfotoProducto !== id))
    } else if (typeof id === 'string') {
      // eliminar por id string
      setImages(images.filter((img: any) => img.id !== id))
    } else if (typeof index === 'number') {
      // eliminar por índice
      setImages(images.filter((_, i) => i !== index))
    }
  }

  const cropImageNow = (id: number) => {
    const { url } = images.find((a: any) => a.id === id)
    const image = new Image(url)
    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    canvas.width = crop!.width
    canvas.height = crop!.height
    const ctx = canvas.getContext('2d')

    const pixelRatio = window.devicePixelRatio
    canvas.width = crop!.width * pixelRatio
    canvas.height = crop!.height * pixelRatio
    ctx!.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    ctx!.imageSmoothingQuality = 'high'

    ctx?.drawImage(
      image,
      crop!.x * scaleX,
      crop!.y * scaleY,
      crop!.width * scaleX,
      crop!.height * scaleY,
      0,
      0,
      crop!.width,
      crop!.height
    )

    // Converting to base64
    const base64Image = canvas.toDataURL('image/jpeg')
    setOutput(base64Image)
  }

  const { getRootProps, open, getInputProps, fileRejections } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    maxFiles: maxPhotos - (images?.length || 0),
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/jpg': []
    }
  })

  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = [...list]
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const handleOfrecerPremium = () => {
    track('vendor_premium_popup_abrir', {
      origin: 'abm_foto_gris'
    })
    onOpen()
  }

  useEffect(() => {
    if (fileRejections.length === 0) return setDropError(undefined)
    fileRejections.map((files: any) =>
      files.errors.map((err: any) => {
        return err.code === DROPPER_ERROR_LABELS.maxPhotos
          ? setDropError(`La cantidad máxima de fotos es: ${maxPhotos}`)
          : err.code === DROPPER_ERROR_LABELS.invalidFile
            ? setDropError('Solamente podés subir archivos JPG, JPEG o PNG')
            : setDropError('Error desconocido')
      })
    )
  }, [fileRejections])

  return (
    <>
      {isMobile && (
        <Button
          onClick={() => (!maxPhotos || images?.length < maxPhotos ? open() : handleOfrecerPremium())}
          disabled={maxPhotos && images?.length >= maxPhotos && !ofrecerPremium}
          variant="text"
          size="large"
          fullWidth
          sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}
        >
          {maxPhotos && images?.length >= maxPhotos ? (
            'Alcanzaste el límite de fotos.' + (ofrecerPremium ? ' Toca para ampliar' : '')
          ) : (
            <>
              Agregar Fotos
              <Add
                color={images?.length >= maxPhotos ? 'disabled' : 'primary'}
                fontSize="medium"
                sx={{ marginBottom: '6px' }}
              />
            </>
          )}
        </Button>
      )}
      <Box
        sx={{
          border: '2px dashed #d9d9d9',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          padding: '20px',
          textAlign: 'center',
          cursor: 'pointer',
          ...(isDragging && {
            borderColor: '#007bff',
            backgroundColor: '#eee',
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, #ccc 5px, #ccc 10px)',
            border: '2px dashed #007bff'
          })
        }}
        {...getRootProps()}
        onDragOver={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
      >
        {!isMobile && (
          <Button
            onClick={() => (images?.length < maxPhotos || !images || !maxPhotos ? open() : onOpen())}
            disabled={images?.length >= maxPhotos && !ofrecerPremium}
            variant="text"
            size="large"
            fullWidth
            sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}
          >
            {images?.length >= maxPhotos
              ? `ALCANZASTE EL MÁXIMO DE FOTOS.${ofrecerPremium ? ' Hacé click para ampliar el límite' : ''}`
              : 'Arrastrá las imágenes o hacé click aquí'}
          </Button>
        )}
        <Box
          sx={{
            textAlign: 'center',
            cursor: 'pointer',
            overflow: 'auto'
          }}
        >
          <input {...getInputProps()} />
          <DragDropContext
            onDragEnd={(result: any) => {
              setIsDragging(false)
              const { source, destination } = result
              if (!destination) return
              if (source.index === destination.index && source.droppableId === destination.droppableId) return
              setImages((imgs: any) => reorder(imgs, source.index, destination.index))
            }}
          >
            <Droppable droppableId="droppableImagesId" direction="horizontal">
              {(drop: any) => (
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    width: 'max-content',
                    height: '150px',
                    scrollDirection: 'horizontal'
                  }}
                  {...drop.droppableProps}
                  ref={drop.innerRef}
                >
                  {images?.map((item: any, i: number) => {
                    return (
                      <Draggable
                        isDragDisabled={images >= maxPhotos ? true : false}
                        key={item.idfotoProducto ? item.idfotoProducto * i : item.id ? item.id : i}
                        draggableId={(item.idfotoProducto ? item.idfotoProducto * i : item.id ? item.id : i).toString()}
                        index={i}
                      >
                        {(drag: any) => (
                          <Box
                            {...drag.draggableProps}
                            ref={drag.innerRef}
                            {...drag.dragHandleProps}
                            sx={{
                              width: '150px',
                              height: '150px',
                              backgroundImage: `url(${item.url_thumb || item.url})`,
                              backgroundSize: 'cover',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              position: 'relative',
                              ...(isMobile
                                ? {
                                    ...(showIcons && {
                                      backgroundColor: 'rgba(248, 249, 250, 1)',
                                      '& > .iconButton': {
                                        opacity: '0.6',
                                        color: 'white',
                                        backgroundColor: 'blue',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 1 }
                                      },
                                      '& > .foreground': {
                                        opacity: '0.8'
                                      }
                                    })
                                  }
                                : {
                                    '&:hover': {
                                      backgroundColor: 'rgba(248, 249, 250, 1)',
                                      '& > .iconButton': {
                                        opacity: '0.6',
                                        color: 'white',
                                        backgroundColor: 'blue',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 1 }
                                      },
                                      '& > .foreground': {
                                        opacity: '0.8'
                                      }
                                    }
                                  })
                            }}
                            onClick={() => {
                              setImgSelected(item.url_thumb || item.url)
                              setShowIcons(!showIcons)
                            }}
                          >
                            <Box
                              className="foreground"
                              sx={{
                                width: '150px',
                                height: '150px',
                                opacity: 0,
                                backgroundColor: 'white',
                                position: 'absolute',
                                gap: '1rem',
                                display: isMobile ? (item.url === imgSelected ? 'flex' : 'none') : undefined
                              }}
                            />
                            {/* Íconos existentes arriba */}
                            <IconButton
                              disabled={isMobile && !showIcons}
                              className="iconButton"
                              aria-label="expand"
                              size="medium"
                              sx={{
                                opacity: 0,
                                marginRight: '10px',
                                display: isMobile ? (item.url === imgSelected ? 'flex' : 'none') : undefined
                              }}
                              onClick={() => setExpandImage(true)}
                            >
                              <VisibilityIcon fontSize="inherit" />
                            </IconButton>

                            <IconButton
                              disabled={isMobile && !showIcons}
                              className="iconButton"
                              aria-label="delete"
                              size="medium"
                              sx={{
                                opacity: 0,
                                display: isMobile ? (item.url === imgSelected ? 'flex' : 'none') : undefined
                              }}
                              onClick={() => handleDelete(item.idfotoProducto || item.id, i)}
                            >
                              <DeleteOutline fontSize="inherit" />
                            </IconButton>

                            {/* Ícono de addText abajo centrado */}
                            {cta && (
                              <IconButton
                                disabled={isMobile && !showIcons}
                                className="iconButton"
                                aria-label="text"
                                size="medium"
                                sx={{
                                  opacity: 0,
                                  position: 'absolute',
                                  bottom: '5px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  display: isMobile ? (item.url === imgSelected ? 'flex' : 'none') : undefined
                                }}
                                onClick={() => {
                                  setIndexSelected(i)
                                  setAddText(true)
                                }}
                              >
                                <TitleIcon fontSize="inherit" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Draggable>
                    )
                  })}
                  {drop.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Box>
      {dropError !== undefined && (
        <Dialog open={dropError !== undefined} onClose={() => setDropError(undefined)} maxWidth="lg">
          <DialogTitle>Errores en la subida de imagenes</DialogTitle>
          <DialogContent>{dropError}</DialogContent>
          <DialogActions>
            <Button onClick={() => setDropError(undefined)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
      <ExpandImageDialog open={expandImage} setExpandImage={setExpandImage} imageUrl={imgSelected} />
      {cta && indexSelected !== null && indexSelected !== undefined && (
        <AddTextDialog
          open={addText}
          setAddText={setAddText}
          desc={desc}
          setDesc={setDesc}
          index={indexSelected}
          images={images}
          setImages={setImages}
        />
      )}
    </>
  )
}

export default Dropzone
